/**
 * O depósito: onde o conjunto de trabalho vive durante a sessão.
 *
 * ⚠️ NÃO HÁ BANCO LOCAL. Escritório e tablet são 100% online (23–24/09/2026):
 * o depósito é `DepositoEmMemoria`, recheado pelo servidor no arranque. O
 * Supabase é a verdade; recarregar não perde nada, porque a verdade desce de
 * novo. A interface `Deposito` continua existindo para o motor de sincronismo
 * (`@pcp/offline`) e os testes serem exercitados sem aparelho e sem rede.
 *
 * Aqui só mora persistência. Nenhuma regra de negócio: quem decide o que fazer
 * com o dado é `repositorio.ts`.
 */

export const COLECOES = {
  os: { chave: 'numero', indices: ['status'] },
  osVersao: { chave: 'id', indices: ['osNumero'] },
  osPlano: { chave: 'id', indices: ['osNumero'] },
  osEtapa: { chave: 'id', indices: ['osNumero', 'estacaoId'] },
  osMaterial: { chave: 'id', indices: ['osNumero'] },
  osEntrega: { chave: 'id', indices: ['osNumero'] },
  evento: { chave: 'id', indices: ['osEtapaId'] },
  expedicao: { chave: 'id', indices: ['osNumero'] },
  importacao: { chave: 'id', indices: ['hashPdf'] },
  alerta: { chave: 'id', indices: ['tipo'] },
  setor: { chave: 'id', indices: [] },
  estacao: { chave: 'id', indices: ['setorId'] },
  dePara: { chave: 'id', indices: [] },
  motivo: { chave: 'id', indices: [] },
  pessoa: { chave: 'id', indices: [] },
  dispositivo: { chave: 'id', indices: [] },
  cliente: { chave: 'id', indices: ['codigoErp'] },
  config: { chave: 'chave', indices: [] },
  arquivo: { chave: 'ref', indices: [] },
  filaSaida: { chave: 'id', indices: [] },
} as const;

export type NomeColecao = keyof typeof COLECOES;

export interface Deposito {
  gravar<T>(colecao: NomeColecao, valor: T): Promise<void>;
  gravarLote<T>(colecao: NomeColecao, valores: readonly T[]): Promise<void>;
  ler<T>(colecao: NomeColecao, chave: string | number): Promise<T | null>;
  listar<T>(colecao: NomeColecao): Promise<T[]>;
  /** Busca por índice declarado em COLECOES. */
  porIndice<T>(colecao: NomeColecao, indice: string, valor: unknown): Promise<T[]>;
  apagar(colecao: NomeColecao, chave: string | number): Promise<void>;
  limpar(colecao: NomeColecao): Promise<void>;
  limparTudo(): Promise<void>;
}

/* ------------------------------------------------------------------ */
/* Em memória — o depósito dos aplicativos e dos testes                */
/* ------------------------------------------------------------------ */

export class DepositoEmMemoria implements Deposito {
  private dados = new Map<string, Map<string, unknown>>();

  private colecao(nome: NomeColecao): Map<string, unknown> {
    let m = this.dados.get(nome);
    if (!m) {
      m = new Map();
      this.dados.set(nome, m);
    }
    return m;
  }

  private chaveDe(colecao: NomeColecao, valor: unknown): string {
    const campo = COLECOES[colecao].chave;
    const k = (valor as Record<string, unknown>)[campo];
    if (k === undefined || k === null) {
      throw new Error(`Registro sem "${campo}" não pode ir para a coleção "${colecao}".`);
    }
    return String(k);
  }

  async gravar<T>(colecao: NomeColecao, valor: T): Promise<void> {
    this.colecao(colecao).set(this.chaveDe(colecao, valor), estruturar(valor));
  }

  async gravarLote<T>(colecao: NomeColecao, valores: readonly T[]): Promise<void> {
    const c = this.colecao(colecao);
    for (const v of valores) c.set(this.chaveDe(colecao, v), estruturar(v));
  }

  async ler<T>(colecao: NomeColecao, chave: string | number): Promise<T | null> {
    return (this.colecao(colecao).get(String(chave)) as T) ?? null;
  }

  async listar<T>(colecao: NomeColecao): Promise<T[]> {
    return [...this.colecao(colecao).values()] as T[];
  }

  async porIndice<T>(colecao: NomeColecao, indice: string, valor: unknown): Promise<T[]> {
    const todos = await this.listar<Record<string, unknown>>(colecao);
    return todos.filter((r) => r[indice] === valor) as T[];
  }

  async apagar(colecao: NomeColecao, chave: string | number): Promise<void> {
    this.colecao(colecao).delete(String(chave));
  }

  async limpar(colecao: NomeColecao): Promise<void> {
    this.colecao(colecao).clear();
  }

  async limparTudo(): Promise<void> {
    this.dados.clear();
  }
}

/** Cópia funda — para o teste flagrar mutação acidental. */
function estruturar<T>(v: T): T {
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(v);
    } catch {
      /* Blob e afins podem não clonar em todo ambiente; segue o de baixo. */
    }
  }
  return v;
}
